import React, { FC, useEffect, useState, useContext, useCallback, useRef } from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import DroppedDevice from "./DroppedDevice";
import useProjectStore, { IDeviceInstance, DistanceUnit, IObstructionInstance } from "common/store/ProjectStore";
import base64toURL from "common/utils/plotPlanUtil";
import { SharedProjectContext } from "components/project/Project";
import ScaleOverlay from "./ScaleOverlay";
import PlotScale from "./PlotScale";
import MultiSelectOverlay from "./MultiSelectOverlay";
import { useAuthContext } from "common/contexts/AuthContext";
import DeviceCategory from "common/constants/DeviceCategory";
import useDeviceStore from "common/store/DeviceStore";
import useNotificationStore from "common/store/NotificationStore";
import useSettingsStore from "common/store/SettingsStore";
import DeviceProperties from "./DeviceProperties";
import ObstructionProperties from "./ObstructionProperties";
import ShowNetwork from "./ShowNetwork";
import ZoomInOut from "common/components/zoom/ZoomInOut";
import DroppedObstruction from "./DroppedObstruction";
import { FeetMetersConversion } from "common/constants/GlobalValues";
import { LanguageContext } from "common/contexts/LanguageContext";
import ObstructionInfo from "./ObstructionInfo";
import { getAllNetworks } from "common/utils/networkExtensions";
import { useAuth0 } from '@auth0/auth0-react';


const PlotBox: FC = () => {
  const [deviceMeta, setDeviceMeta] = useState<
    Map<number, { selected: boolean; initial: boolean; neighbors: number[]; path: boolean }>
  >(new Map());
  const [plotImage, setPlotImage] = useState<string>("");
  const [plotImageTransform, setPlotImageTransform] = useState<number>(1);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [propertiesDeviceInstanceID, setPropertiesDeviceInstanceID] = useState<number | null>(null);
  const [propertiesObstructionInstanceID, setPropertiesObstructionInstanceID] = useState<number | null>(null);
  const [obstructionPropsOpen, setObstructionPropsOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ top: 0, left: 0 });
  const [contextDropPosition, setContextDropPosition] = useState({ top: 0, left: 0 });
  const [isContextMenuOpen, setContextMenuOpen] = useState(false);
  const { getString } = useContext(LanguageContext);
  const [openObstructionInfo, setOpenObstructionInfo] = useState<boolean>(false);
  const [currentScalingFactor, setCurrentScalingFactor] = useState<number | null>(null);
  const [imageMaxHeight, setImageMaxHeight] = useState<number | null>(null);

  const plotBoxRef = useRef<HTMLDivElement>(null);

  //Handle deviceMeta (selected & initial values)
  const addDeviceMeta = (key: number, value: { selected: boolean; initial: boolean }) => {
    setDeviceMeta(
      (prevMap) =>
        new Map(prevMap.set(key, { selected: value.selected, initial: value.initial, neighbors: [], path: true })),
    );
    //manage compliance alongside the deviceMeta (compliance is an extension of deviceMeta)
    setCompliance((prevMap) => new Map(prevMap.set(key, { compliant: true })));
  };
  const updateDeviceMeta = (
    key: number,
    value: { selected: boolean; initial: boolean; neighbors: number[]; path: boolean },
  ) => {
    setDeviceMeta((prevMeta) => {
      const updatedMeta = new Map(prevMeta);
      updatedMeta.set(key, value);
      return updatedMeta;
    });
  };
  const deleteDeviceMeta = (key: number) => {
    setDeviceMeta((prevMeta) => {
      const updatedMeta = new Map(prevMeta);
      updatedMeta.delete(key);
      return updatedMeta;
    });
    setCompliance((prevCompliance) => {
      const updatedCompliance = new Map(prevCompliance);
      updatedCompliance.delete(key);
      return updatedCompliance;
    });
  };
  const resetDeviceMeta = () => {
    setDeviceMeta(new Map());
    setCompliance(new Map());
  };
  const updateSelected = (id: number, b: boolean) => {
    updateDeviceMeta(id, {
      selected: b,
      initial: deviceMeta.get(id)?.initial ?? false,
      neighbors: deviceMeta.get(id)?.neighbors ?? [],
      path: deviceMeta.get(id)?.path ?? true,
    });
  };
  const updateInitial = (id: number, b: boolean) => {
    updateDeviceMeta(id, {
      selected: deviceMeta.get(id)?.selected ?? false,
      initial: b,
      neighbors: deviceMeta.get(id)?.neighbors ?? [],
      path: deviceMeta.get(id)?.path ?? true,
    });
  };
  const updateNeighbors = (id: number, n: number[]) => {
    updateDeviceMeta(id, {
      selected: deviceMeta.get(id)?.selected ?? false,
      initial: deviceMeta.get(id)?.initial ?? false,
      neighbors: n,
      path: deviceMeta.get(id)?.path ?? true,
    });
    setNeighborsUpdated((prevCount) => prevCount + 1);
  };
  const updatePath = (id: number, b: boolean) => {
    updateDeviceMeta(id, {
      selected: deviceMeta.get(id)?.selected ?? false,
      initial: deviceMeta.get(id)?.initial ?? false,
      neighbors: deviceMeta.get(id)?.neighbors ?? [],
      path: b,
    });
  };

  const {
    projectID,
    loading,
    plotPlanImage,
    imageFileType,
    zoomPercent,
    iconPercent,
    scalingFactor,
    addDeviceInstance,
    deleteDeviceInstance,
    activeNetwork,
    activeNetworkID,
    activeGateway,
    activeNetworkContainerID,
    activeNetworkContainer,
    setGateway,
    updateGateway,
    transparencyPercent,
    enableStressTest,
    distanceID,
    setScalingFactor,
    networkContainers,
    obstructionInstances,
    addObstructionInstance,
    updateDeviceInstances,
    addNetwork,
  } = useProjectStore();

  const settings = useSettingsStore((state) => state.settings);
  const lookupCategory = useDeviceStore((state) => state.lookupCategory);
  const lookupUpdateRate = useDeviceStore((state) => state.lookupUpdateRate);
  const enqueueNotification = useNotificationStore((state) => state.enqueueNotification);
  const updateCurrentBatchID = useNotificationStore((state) => state.updateCurrentBatchID);
  const batchID = useNotificationStore((state) => state.currentBatchID);
  const {
    scaleSet,
    multiSelect,
    setCopy,
    setPaste,
    setDeleteSelected,
    projectSaved,
    setProjectSaved,
    calculationsToggle,
    networkDeleted,
    setNetworkDeleted,
    networkCheck,
    setNetworkCheck,
    setNetworkHealth,
    triggerCalculations,
    setTriggerCalculations,
  } = useContext(SharedProjectContext);
    const { isAuthenticatedUser } = useAuthContext();

  const [selectMultiple, setSelectMultiple] = useState<boolean>(false);
  const [selectMetadata, setSelectMetadata] = useState<{ currentID: number; multiselect: boolean }>();
  const [toCopy, setToCopy] = useState<IDeviceInstance[]>([]);
  const [coordsUpdated, setCoordsUpdated] = useState<{ updated: boolean; index: number }>({
    updated: false,
    index: -1,
  });
  const [updateComplete, setUpdateComplete] = useState<number>(0);
  const [neighborsUpdated, setNeighborsUpdated] = useState<number>(0);
  const firstRender = useRef(true);
  const [measurementUnit, setMeasurementUnit] = useState<number>();
  const [obstructionUpdated, setObstructionUpdated] = useState<{ updated: boolean; index: number }>({
    updated: false,
    index: -1,
  });
  const [obUpdateComplete, setObUpdateComplete] = useState<number>(0);

  const setCoordsUpdatedComplete = () => {
    setUpdateComplete((prevCount) => prevCount + 1);
  };

  const [multiDrag, setMultiDrag] = useState<number[]>([]); //has the deviceInstanceIDs of the devices being dragged
  const [compliance, setCompliance] = useState<Map<number, { compliant: boolean }>>(new Map());
  const updateCompliance = (id: number, b: boolean) => {
    setCompliance((prev) => {
      const updated = new Map(prev);
      updated.set(id, { compliant: b });
      return updated;
    });
  };

  useEffect(() => {
    if (
      activeNetwork &&
      updateComplete >= activeNetwork.deviceInstances.length + 1 &&
      neighborsUpdated === deviceMeta.size - 1
    ) {
      setUpdateComplete(0);
      setNeighborsUpdated(0);
      verifyPath();
      setCoordsUpdated({ updated: false, index: -1 });
      updateCurrentBatchID();
    }
  }, [updateComplete, neighborsUpdated]);

  const setObstructionUpdatedComplete = () => {
    setObUpdateComplete((prevCount) => prevCount + 1);
  };

  useEffect(() => {
    if (
      activeNetwork &&
      obUpdateComplete >= activeNetwork.deviceInstances.length + 1 &&
      neighborsUpdated === deviceMeta.size - 1
    ) {
      setObUpdateComplete(0);
      setNeighborsUpdated(0);
      setObstructionUpdated({ updated: false, index: -1 });
      verifyPath();
      updateCurrentBatchID();
    }
  }, [obUpdateComplete]);

  //Network changed or saved for the first time, also on first fetch
  useEffect(() => {
    setToCopy([]);
    resetDeviceMeta();
    if (activeGateway) {
      addDeviceMeta(activeGateway.deviceInstanceID, { selected: false, initial: false });
    }
    activeNetwork?.deviceInstances.forEach((device) => {
      addDeviceMeta(device.deviceInstanceID, { selected: false, initial: false });
    });
  }, [activeNetwork?.networkID]); // trigger ONLY on network change (networkID is changed)

  //Single network deleted when user is not authenticated
  useEffect(() => {
      if (!isAuthenticatedUser && networkDeleted) {
      setToCopy([]);
      resetDeviceMeta();
      if (activeGateway) {
        addDeviceMeta(activeGateway.deviceInstanceID, { selected: false, initial: false });
      }
      activeNetwork?.deviceInstances.forEach((device) => {
        addDeviceMeta(device.deviceInstanceID, { selected: false, initial: false });
      });
    }
    setNetworkDeleted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkDeleted]);

  // should run on first render, or if plotPlanImage/imageFileType are changed
  useEffect(() => {
    try {
      // will not fetch the project if it is already loaded
        if (isAuthenticatedUser && projectID !== null) {
        setPlotImage(base64toURL(plotPlanImage ?? null, imageFileType ?? null));
        } else if (!isAuthenticatedUser && (plotPlanImage !== null || imageFileType !== null)) {
        setPlotImage(base64toURL(plotPlanImage ?? null, imageFileType ?? null));
      }
    } catch (err) {
      console.log("ERROR!!:", err);
    }
  }, [projectID, plotPlanImage, imageFileType, isAuthenticatedUser, settings]);

  useEffect(() => {
    var plotElement = document.getElementById("plot-plan-view-port");
    var footerElement = document.getElementById("emerson-footer");
    if (plotElement && footerElement) {
      var plot = plotElement.getBoundingClientRect();
      var footer = footerElement.getBoundingClientRect();

      var maxHeight = footer.top - plot.top - 20;
      setImageMaxHeight(maxHeight);
    }
  }, [plotImageTransform]);

  useEffect(() => {
    if (networkCheck) {
      var check = true;
      deviceMeta.forEach((value, key) => {
        if (!value.path && !compliance.get(key)?.compliant) {
          check = false;
          return;
        }
      });
      setNetworkHealth(check);
    }
    setNetworkCheck(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkCheck]);

  useEffect(() => {
    if (triggerCalculations) {
      setCoordsUpdated({ updated: true, index: -1 });
    }
    setTriggerCalculations(false);
  }, [setTriggerCalculations, triggerCalculations]);

  // Functions to handle draggable
  function handleOnDrop(e: React.DragEvent) {
    const droppable = e.dataTransfer.getData("droppableDevice");
    if (droppable && droppable === "true") {
      const deviceImage = e.dataTransfer.getData("deviceImage") as string;
      const deviceID = e.dataTransfer.getData("deviceID") as string;
      //check for check for second antenna, gateway + antenna rules
      const deviceCat = lookupCategory(+deviceID);
      if (deviceCat === DeviceCategory.Antenna && activeNetwork!.deviceInstances.length >= 1 && activeNetworkContainer!.networks.length === 1) {
        addSecondAntenna(deviceID, deviceImage, e.clientX, e.clientY);
      }
      else if (verifyDevice(deviceCat)) {
        var dev: IDeviceInstance = {
          deviceInstanceID: -999,
          deviceID: parseInt(deviceID),
          deviceTag: "",
          xPosition: e.clientX,
          yPosition: e.clientY,
          zPosition: 0,
          range:
            deviceCat === DeviceCategory.Gateway
              ? distanceID === DistanceUnit.Feet
                ? 1300
                : 396.2
              : distanceID === DistanceUnit.Feet
                ? 250
                : 76.2,
          height: 0,
          updatedDate: new Date(),
          image: deviceImage,
          updateRateID: lookupUpdateRate(+deviceID).defaultUpdateRateID,
          note: null,
          initial: true,
          rowVersion: null,
        };
        if (deviceCat === DeviceCategory.Gateway) {
          var newID = setGateway(dev);
        } else {
          newID = addDeviceInstance(dev, activeNetworkID);
        }
        addDeviceMeta(newID, { selected: false, initial: true });
      }
    } else if (droppable && droppable === "false") {
      //is obstruction
      if (scalingFactor) {
        const deviceID = e.dataTransfer.getData("deviceID") as string;
        const w = 50; //default width
        const l = 40; // default length
        var boxElement = document.getElementById("drop-box");
        var x = e.clientX;
        var y = e.clientY;
        if (boxElement) {
          var box = boxElement.getBoundingClientRect();
          x = e.clientX - box.left + boxElement.scrollLeft;
          y = e.clientY - box.top + boxElement.scrollTop;
        }
        var ob: IObstructionInstance = {
          obstructionInstanceID: -999,
          obstructionTypeID: parseInt(deviceID),
          projectID: projectID!,
          obstructionName: "Obstruction",
          xPosition: x / (zoomPercent / 100) / plotImageTransform,
          yPosition: y / (zoomPercent / 100) / plotImageTransform,
          zPosition: 0,
          height: 0,
          width: w,
          length: l,
          updatedDate: null,
          initial: true,
          rowVersion: null,
        };
        var ind = obstructionInstances.length;
        addObstructionInstance(ob);
        setObstructionUpdated({ updated: true, index: ind });
        setOpenObstructionInfo(true);
      } else {
        // obstructions are dependent on length/width (and therefore the scaling factor)
        //  for obstructions to function without scaling factor being set
        const message = getString("nwk_rules_scaling_factor_ob ");
        enqueueNotification(message.replace(/\s/g, ""), message, "error");
      }
    }
  }

  function addSecondAntenna(deviceID: string, deviceImage: string, x: number, y: number) {
    var newID = addNetwork(activeNetworkContainerID);
    var dev: IDeviceInstance = {
      deviceInstanceID: -999,
      deviceID: parseInt(deviceID),
      deviceTag: "",
      xPosition: x,
      yPosition: y,
      zPosition: 0,
      range: distanceID === DistanceUnit.Feet? 250: 76.2,
      height: 0,
      updatedDate: new Date(),
      image: deviceImage,
      updateRateID: lookupUpdateRate(+deviceID).defaultUpdateRateID,
      note: null,
      initial: true,
      rowVersion: null,
    };
    addDeviceInstance(dev, newID);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  useEffect(() => {
    if (activeNetwork?.deviceInstances && activeNetwork.deviceInstances.length > 0) {
      setCoordsUpdated({ updated: true, index: -1 });
    }
  }, [scalingFactor, enableStressTest]); //triggers only on scalingFactor or enableStressTest being updated (also on mount)

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      setMeasurementUnit(distanceID);
    } else if (currentScalingFactor === null || scalingFactor !== currentScalingFactor) {
      setCurrentScalingFactor(scalingFactor);
    } else {
      if (measurementUnit !== distanceID && scalingFactor) {
        if (distanceID === DistanceUnit.Feet) {
          //convert from meters to feet
          setScalingFactor(scalingFactor / FeetMetersConversion);
          getAllNetworks(networkContainers).forEach((network) => {
            if (network.networkID !== activeNetworkID) {
              network.deviceInstances.forEach((instance) => {
                var dev = { ...instance };
                dev.range = dev.range * FeetMetersConversion;
                dev.height = dev.height * FeetMetersConversion;
                updateDeviceInstances(network.networkID, dev);
              });
            }
          });
          networkContainers.forEach((container) => {
            if (container.networkContainerID !== activeNetworkContainerID) {
              var dev = { ...container.gatewayDeviceInstance! };
              dev.range = dev.range! * FeetMetersConversion;
              dev.height = dev.height! * FeetMetersConversion;
              updateGateway(activeNetworkContainerID, dev);
            }
          });
        } else {
          //convert from feet to meters
          setScalingFactor(scalingFactor * FeetMetersConversion);
          getAllNetworks(networkContainers).forEach((network) => {
            if (network.networkID !== activeNetworkID) {
              network.deviceInstances.forEach((instance) => {
                var dev = { ...instance };
                dev.range = dev.range / FeetMetersConversion;
                dev.height = dev.height / FeetMetersConversion;
                updateDeviceInstances(network.networkID, dev);
              });
            }
          });
          networkContainers.forEach((container) => {
            if (container.networkContainerID !== activeNetworkContainerID) {
              var dev = { ...container.gatewayDeviceInstance! };
              dev.range = dev.range! / FeetMetersConversion;
              dev.height = dev.height! / FeetMetersConversion;
              updateGateway(activeNetworkContainerID, dev);
            }
          });
        }
        setMeasurementUnit(distanceID);
      }
    }
  }, [distanceID, scalingFactor]);

  const unselectDevices = () => {
    if (selectMetadata) {
      if (selectMetadata.multiselect) {
        deviceMeta.forEach((value, key) => {
          if (value.selected === true) {
            updateSelected(key, false);
          }
        });
      } else {
        if (deviceMeta.has(selectMetadata.currentID)) {
          updateSelected(selectMetadata.currentID, false);
        }
      }
    }
  };

  //A gateway must be the first device added (only one allowed per network),
  // an antenna must be the second device added (only one allowed per network)
  function verifyDevice(cat: number) {
    var ret = false;
    var message = "";
    var gatewayAdded = activeGateway !== null && activeGateway !== undefined;
    var antennaAdded = activeNetwork!.deviceInstances.length >= 1;
    if (gatewayAdded && cat === DeviceCategory.Gateway) {
      //error: only one gateway allowed
      message = getString("nwk_rules_one_gateway");
      ret = false;
    } else if (antennaAdded && cat === DeviceCategory.Antenna) {
      //error: only one antenna allowed
      message = getString("nwk_rules_one_antenna");
      ret = false;
    } else if (gatewayAdded && antennaAdded) {
      ret = true;
    } else if (!gatewayAdded) {
      if (cat === DeviceCategory.Gateway) {
        ret = true;
      } else {
        //error: must add a gateway first
        message = getString("nwk_rules_gateway_first");
        ret = false;
      }
    } else if (!antennaAdded) {
      if (cat === DeviceCategory.Antenna) {
        ret = true;
      } else {
        //error: must add a antenna
        message = getString("nwk_rules_antenna_second");
        ret = false;
      }
    }
    if (!ret) {
      enqueueNotification(message.replace(/\s/g, ""), message, "error");
    }
    return ret;
  }

  useEffect(() => {
    if (calculationsToggle) {
      setCoordsUpdated({ updated: true, index: -1 });
    }
  }, [calculationsToggle]);

  //handle validation: all instruments must have a path to the antenna
  function verifyPath() {
    if (activeNetwork!.deviceInstances.length < 2 || deviceMeta.size !== activeNetwork!.deviceInstances.length + 1) {
      return;
    }
    var toVisit = new Map<number, number>();
    toVisit.set(activeNetwork!.deviceInstances[0].deviceInstanceID, 0);
    //unvisited: Map containing index (value) & id (key)
    var unvisited = new Map<number, number>();
    activeNetwork?.deviceInstances.forEach((device, index) => {
      //lookupCategory not Gateway or Antenna (index > 0)
      if (index > 0) {
        unvisited.set(device.deviceInstanceID, index);
      }
    });
    //visited: empty Map (same structure as unvisited)
    var visited = new Map<number, number>();
    while (toVisit.size > 0) {
      const atID = toVisit.keys().next().value;
      const atIndex = toVisit.get(atID);
      deviceMeta.get(atID)?.neighbors.forEach((neighbor) => {
        var neighborID = activeNetwork!.deviceInstances[neighbor].deviceInstanceID;
        var adjacent = deviceMeta.get(neighborID!)?.neighbors; //adjacent is an array of the neighbor's neighbors' indexs
        var adjacentIndex = adjacent!.indexOf(atIndex!);
        if (adjacentIndex >= 0 && unvisited.get(neighborID) !== undefined && toVisit.get(neighborID) === undefined) {
          toVisit.set(neighborID, neighbor);
        }
      });
      //add 'at' to visited, delete 'at' from unvisited
      visited.set(atID, atIndex!);
      unvisited.delete(atID);
      //remove first element from toVisit
      toVisit.delete(atID);
    }
    //whatever is left in unvisited has no path
    unvisited.forEach((value, key) => {
      updatePath(key, false);
    });
    //anything in visited has a path
    visited.forEach((value, key) => {
      updatePath(key, true);
    });
    if (unvisited.size > 0) {
      var message = "";
      if (unvisited.size > 1) {
        message = unvisited.size.toString() + " " + getString("nwk_rules_no_path_plural");
      } else {
        message = unvisited.size.toString() + " " + getString("nwk_rules_no_path");
      }
      enqueueNotification(message.replace(/\s/g, ""), message, "warning", batchID);
    }
  }

  //handle right-click context menu
  const handleRightClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault(); //Note: comment this for checking styles when debugging
    setSelectMultiple(false);
  };

  const handleContextMenuForImage = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    event.stopPropagation();

    if (toCopy.length > 0 && event.target instanceof HTMLElement) {
      const { clientX, clientY } = event;
      setContextMenuPosition({ top: clientY, left: clientX });
      setContextMenuOpen(true);
      setContextDropPosition({ left: clientX, top: clientY });
    }
  };

  // functions to copy and paste
  const handleCopy = useCallback(() => {
    if (activeNetwork === null) {
      console.log("activeNetwork is null, ignoring copy");
      return;
    }
    setToCopy([]);
    activeNetwork.deviceInstances.forEach((device) => {
      const categoryID = lookupCategory(device.deviceID);
      if (
        deviceMeta.get(device.deviceInstanceID)?.selected &&
        categoryID !== DeviceCategory.Gateway &&
        categoryID !== DeviceCategory.Antenna
      ) {
        var dev: IDeviceInstance = {
          deviceInstanceID: -999,
          deviceID: device.deviceID,
          deviceTag: device.deviceTag + " Copy",
          xPosition: device.xPosition,
          yPosition: device.yPosition,
          zPosition: 0,
          range: device.range,
          height: device.height,
          updatedDate: null,
          image: device.image,
          initial: false,
          updateRateID: device.updateRateID,
          note: null,
          rowVersion: null,
        };
        setToCopy((prevCopy) => [...prevCopy, dev]);
      }
    });
  }, [activeNetwork, deviceMeta, lookupCategory, lookupUpdateRate]);

  const handleSingleCopy = useCallback(
    (device: IDeviceInstance) => {
      if (!device) {
        console.log("device not found, ignoring copy");
        return;
      }
      const categoryID = lookupCategory(device.deviceID);
      if (categoryID !== DeviceCategory.Gateway && categoryID !== DeviceCategory.Antenna) {
        var dev: IDeviceInstance = {
          deviceInstanceID: -999,
          deviceID: device.deviceID,
          deviceTag: "",
          xPosition: device.xPosition,
          yPosition: device.yPosition,
          zPosition: 0,
          range: 250,
          height: 0,
          updatedDate: null,
          image: device.image,
          initial: false,
          updateRateID: device.updateRateID,
          note: null,
          rowVersion: null,
        };
        setToCopy([dev]);
      }
    },
    [lookupCategory],
  );

  const handlePaste = useCallback(() => {
    toCopy.forEach((device) => {
      var dev: IDeviceInstance = {
        deviceInstanceID: -999,
        deviceID: device.deviceID,
        deviceTag: "",
        xPosition: device.xPosition + 40 * (iconPercent / 100),
        yPosition: device.yPosition,
        zPosition: 0,
        range: 250,
        height: 0,
        updatedDate: null,
        image: device.image,
        initial: false,
        updateRateID: device.updateRateID,
        note: null,
        rowVersion: null,
      };
      var newID = addDeviceInstance(dev, activeNetworkID);
      addDeviceMeta(newID, { selected: false, initial: false });
    });
  }, [toCopy, activeNetworkID, iconPercent, addDeviceInstance]);

  const handlePasteCommand = useCallback(() => {
    toCopy.forEach((device) => {
      var dev: IDeviceInstance = {
        deviceInstanceID: -999,
        deviceID: device.deviceID,
        deviceTag: "",
        xPosition: contextDropPosition.left,
        yPosition: contextDropPosition.top,
        zPosition: 0,
        range: 250,
        height: 0,
        updatedDate: null,
        image: device.image,
        initial: true,
        updateRateID: device.updateRateID,
        note: null,
        rowVersion: null,
      };
      var newID = addDeviceInstance(dev, activeNetworkID);
      addDeviceMeta(newID, { selected: false, initial: false });
    });
  }, [toCopy, activeNetworkID, addDeviceInstance, contextDropPosition]);

  const handlePropertiesRequest = (deviceInstanceID: number) => {
    setPropertiesDeviceInstanceID(deviceInstanceID);
    setPropertiesOpen(true);
  };

  const handleObstructionPropertiesRequest = (obstructionInstanceID: number) => {
    setPropertiesObstructionInstanceID(obstructionInstanceID);
    setObstructionPropsOpen(true);
  };

  useEffect(() => {
    setCopy(() => handleCopy);
    setPaste(() => handlePaste);
  }, [setCopy, setPaste, handleCopy, handlePaste]);

  //function to remove device
  const deleteDevice = (ind: number) => {
    //IF device type is NOT a Gateway then delete
    const categoryID = lookupCategory(activeNetwork!.deviceInstances[ind].deviceID);
    if (categoryID !== DeviceCategory.Gateway && categoryID !== DeviceCategory.Antenna) {
      const id = activeNetwork!.deviceInstances[ind].deviceInstanceID;
      deleteDeviceInstance(id, activeNetworkID);
      deleteDeviceMeta(id);
    }
    setCoordsUpdated({ updated: true, index: -1 });
  };

  //function to remove multiple devices
  const deleteSelectedDevices = useCallback(() => {
    var toDelete: number[] = [];
    activeNetwork?.deviceInstances.forEach((device) => {
      const categoryID = lookupCategory(device.deviceID);
      if (deviceMeta.get(device.deviceInstanceID)?.selected && categoryID !== DeviceCategory.Antenna) {
        toDelete = [...toDelete, device.deviceInstanceID];
      }
    });
    toDelete.forEach((device) => {
      deleteDeviceInstance(device, activeNetworkID);
      deleteDeviceMeta(device);
    });
    setCoordsUpdated({ updated: true, index: -1 });
  }, [deviceMeta, activeNetworkID, deleteDeviceInstance, lookupCategory, activeNetwork]);

  // set delete function to the delete button
  useEffect(() => {
    setDeleteSelected(() => deleteSelectedDevices);
  }, [setDeleteSelected, deleteSelectedDevices]);

  // Functions to handle multi-select
  const keysPressed = useRef(false);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey && (e.key === "c" || e.key === "C")) {
        e.preventDefault();
        handleCopy();
      } else if (e.ctrlKey && (e.key === "v" || e.key === "V")) {
        e.preventDefault();
        handlePaste();
      } else if (e.ctrlKey && e.shiftKey && !keysPressed.current) {
        setSelectMultiple(true);
        keysPressed.current = true;
      } else if (e.key === "Delete") {
        deleteSelectedDevices();
      }
    },
    [deleteSelectedDevices, handleCopy, handlePaste],
  );

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if ((!e.ctrlKey || !e.shiftKey) && keysPressed.current) {
      setSelectMultiple(false);
      keysPressed.current = false;
    }
  }, []);

  // window event handlers to track keyup + keydown
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyUp, handleKeyDown]);

  useEffect(() => {
    if (projectSaved) {
      resetDeviceMeta();
      if (activeGateway) {
        addDeviceMeta(activeGateway.deviceInstanceID, { selected: false, initial: false });
      }
      activeNetwork?.deviceInstances.forEach((device) => {
        addDeviceMeta(device.deviceInstanceID, { selected: false, initial: false });
      });
      setProjectSaved(false);
    }
  }, [projectSaved]);

  useEffect(() => {
    let width = 1;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        width = entry.contentRect.width;
        const img = document.getElementById("plot-image") as HTMLImageElement;
        if (img && img.width > 0 && img.naturalWidth > 0) {
          setPlotImageTransform(width / img.naturalWidth);
        }
      }
    });

    if (plotBoxRef.current) {
      resizeObserver.observe(plotBoxRef.current);
    }

    // Cleanup function to stop observing when the component unmounts
    const containerRefCurrent = plotBoxRef.current;
    return () => {
      if (containerRefCurrent) {
        resizeObserver.unobserve(containerRefCurrent);
      }
    };
  }, []); // Empty dependency array means this effect runs once after the initial render

  return (
    <>
      <ZoomInOut minZoomPercent={100} maxZoomPercent={300} zoomPercentStep={20} draggableElementId="plot-image">
        <Box
          ref={plotBoxRef}
          className="plot-box"
          sx={{
            flex: "1",
            bgcolor: "lightgrey",
            overflow: "hidden",
          }}
          style={{ position: "relative" }}
          id="drop-box"
          onDrop={handleOnDrop}
          onDragOver={handleDragOver}
          onContextMenu={handleRightClick}
        >
          {projectID !== null && !loading && (
            <>
              {
                <img
                  id="plot-image"
                  src={plotImage}
                  alt="plot"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    zIndex: "1",
                    userSelect: "none",
                    opacity: (100 - transparencyPercent) / 100,
                    maxHeight: imageMaxHeight ?? "60vh",
                  }}
                  draggable="false"
                  onContextMenu={handleContextMenuForImage}
                />
              }
              {deviceMeta.size > 0 &&
                activeGateway &&
                !projectSaved && (
                  <DroppedDevice
                    key={activeGateway.deviceInstanceID}
                    name={activeGateway.deviceTag}
                    imageUrl={activeGateway.image}
                    initialX={activeGateway.xPosition}
                    initialY={activeGateway.yPosition}
                    networkID={activeNetworkID}
                    instanceID={activeGateway.deviceInstanceID}
                    instanceIndex={-1}
                    compliant={compliance?.get(activeGateway.deviceInstanceID)?.compliant ?? true}
                    setCompliant={(newBool: boolean) => updateCompliance(activeGateway.deviceInstanceID, newBool)}
                    selected={deviceMeta?.get(activeGateway.deviceInstanceID)?.selected ?? false}
                    setSelected={(newBool: boolean) => updateSelected(activeGateway.deviceInstanceID, newBool)}
                    unselectDevices={unselectDevices}
                    setSelectMetadata={setSelectMetadata}
                    initial={activeGateway.initial}
                    deleteDevice={deleteDevice}
                    coordsUpdated={coordsUpdated}
                    setCoordsUpdated={setCoordsUpdated}
                    setUpdateComplete={setCoordsUpdatedComplete}
                    handlePropertiesRequest={() => handlePropertiesRequest(activeGateway.deviceInstanceID)}
                    handleCopy={() => handleSingleCopy(activeGateway)}
                    setNeighbors={(n: number[]) => updateNeighbors(activeGateway.deviceInstanceID, n)}
                    path={deviceMeta?.get(activeGateway.deviceInstanceID)?.path ?? true}
                    obstructionUpdated={obstructionUpdated}
                    setObstructionUpdated={setObstructionUpdated}
                    setObUpdateComplete={setObstructionUpdatedComplete}
                    plotImageTransform={plotImageTransform}
                    deviceMeta={deviceMeta}
                    multiDrag={multiDrag}
                    setMultiDrag={setMultiDrag}
                  />
                )}
              {deviceMeta.size - 1 === activeNetwork?.deviceInstances?.length &&
                !projectSaved &&
                activeNetwork?.deviceInstances.map((device, index) => (
                  <DroppedDevice
                    key={device.deviceInstanceID}
                    name={device.deviceTag}
                    imageUrl={device.image}
                    initialX={device.xPosition}
                    initialY={device.yPosition}
                    networkID={activeNetworkID}
                    instanceID={device.deviceInstanceID}
                    instanceIndex={index}
                    compliant={compliance?.get(device.deviceInstanceID)?.compliant ?? true}
                    setCompliant={(newBool: boolean) => updateCompliance(device.deviceInstanceID, newBool)}
                    selected={deviceMeta?.get(device.deviceInstanceID)?.selected ?? false}
                    setSelected={(newBool: boolean) => updateSelected(device.deviceInstanceID, newBool)}
                    unselectDevices={unselectDevices}
                    setSelectMetadata={setSelectMetadata}
                    initial={device.initial}
                    deleteDevice={deleteDevice}
                    coordsUpdated={coordsUpdated}
                    setCoordsUpdated={setCoordsUpdated}
                    setUpdateComplete={setCoordsUpdatedComplete}
                    handlePropertiesRequest={() => handlePropertiesRequest(device.deviceInstanceID)}
                    handleCopy={() => handleSingleCopy(device)}
                    setNeighbors={(n: number[]) => updateNeighbors(device.deviceInstanceID, n)}
                    path={deviceMeta?.get(device.deviceInstanceID)?.path ?? true}
                    obstructionUpdated={obstructionUpdated}
                    setObstructionUpdated={setObstructionUpdated}
                    setObUpdateComplete={setObstructionUpdatedComplete}
                    plotImageTransform={plotImageTransform}
                    deviceMeta={deviceMeta}
                    multiDrag={multiDrag}
                    setMultiDrag={setMultiDrag}
                  />
                ))}
              {obstructionInstances.map((obs, index) => (
                <DroppedObstruction
                  key={obs.obstructionInstanceID}
                  instanceIndex={index}
                  instanceID={obs.obstructionInstanceID}
                  initialX={obs.xPosition}
                  initialY={obs.yPosition}
                  handlePropertiesRequest={() => handleObstructionPropertiesRequest(obs.obstructionInstanceID)}
                  setObstructionUpdated={setObstructionUpdated}
                  plotImageTransform={plotImageTransform}
                />
              ))}
              {scaleSet && <ScaleOverlay plotImageTransform={plotImageTransform} />}
              {(selectMultiple || multiSelect) && (
                <MultiSelectOverlay
                  setSelected={updateSelected}
                  setSelectMetadata={() => {
                    setSelectMetadata({ currentID: -999, multiselect: true });
                  }}
                  plotImageTransform={plotImageTransform}
                />
              )}
              {getAllNetworks(networkContainers).map((net, index) => {
                if (net.metaData.isVisible && net.networkID !== activeNetworkID) {
                  return (
                    <ShowNetwork key={index} gateway={networkContainers.find(c => c.networkContainerID === net.networkContainerID)!.gatewayDeviceInstance} devices={net.deviceInstances} plotImageTransform={plotImageTransform} />
                  );
                }
                return null;
              })}
                          {activeNetwork && propertiesDeviceInstanceID && activeNetwork.deviceInstances.some(di => di.deviceInstanceID === propertiesDeviceInstanceID) && propertiesOpen && (
                <DeviceProperties
                  networkID={activeNetwork.networkID}
                  instanceID={propertiesDeviceInstanceID}
                  open={propertiesOpen}
                  setOpen={setPropertiesOpen}
                  rangeUpdated={setCoordsUpdated}
                  deleteDeviceMeta={deleteDeviceMeta}
                />
              )}
              {obstructionInstances.length > 0 && propertiesObstructionInstanceID && (
                <ObstructionProperties
                  instanceID={propertiesObstructionInstanceID}
                  isOpen={obstructionPropsOpen}
                  setIsOpen={setObstructionPropsOpen}
                />
              )}
              <ObstructionInfo isOpen={openObstructionInfo} setOpen={() => setOpenObstructionInfo(false)} />
            </>
          )}
          <Menu
            open={isContextMenuOpen}
            onClose={() => {
              setContextMenuOpen(false);
            }}
            anchorReference="anchorPosition"
            anchorPosition={contextMenuPosition}
          >
            <MenuItem
              onClick={() => {
                setContextMenuOpen(false);
                handlePasteCommand();
              }}
            >
              Paste
            </MenuItem>
          </Menu>
        </Box>
      </ZoomInOut>
      {scalingFactor && scalingFactor > 0 ? <PlotScale plotImageTransform={plotImageTransform} /> : null}
    </>
  );
};

export default PlotBox;
